import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { SimpleSelectField } from '@unite-us/app-components';
import _ from 'lodash';
import { makeCustomOption } from './utils';

/**
 * Builds the custom option from user input, unless custom values are disabled.
 */
const buildCustomOption = ({
  disableCustomValue,
  labelKey,
  options,
  search,
  valueKey,
}) => (
  disableCustomValue ? {} : makeCustomOption({
    labelKey, options, search, valueKey,
  })
);

/**
 * Merges the custom option (if any) with the base options, then filters
 * by the current search term when not using server-side pagination.
 */
const buildFilteredOptions = ({
  customOption,
  options,
  searchTerm,
  canPaginateNetworkGroups,
  labelKey,
}) => {
  const displayOptions = _.compact([
    !_.isEmpty(customOption) ? customOption : null,
    ...options,
  ]);

  if (searchTerm && !canPaginateNetworkGroups) {
    return _.filter(displayOptions, (o) => _.includes(_.toLower(o[labelKey]), _.toLower(searchTerm)));
  }
  return displayOptions;
};

function SingleSelectWithCustomValue({
  canPaginateNetworkGroups,
  className,
  debouncedSearchNetworkGroups,
  disableCustomValue,
  error,
  groupsOptionType,
  hideLabel,
  id,
  label,
  labelKey,
  onBlur,
  onChange,
  options,
  placeholder,
  resetOptions: shouldResetOptions,
  searchPlaceholderValue,
  shouldSearchOnChange,
  value,
  valueKey,
}) {
  const [customOption, setCustomOption] = useState({});
  const searchRef = useRef('');

  const handleSearch = useCallback((search) => {
    searchRef.current = search;
    setCustomOption(buildCustomOption({
      disableCustomValue, labelKey, options, search, valueKey,
    }));

    // If the feature flag is ON, do a debounced search including the input text
    if (canPaginateNetworkGroups && _.isFunction(debouncedSearchNetworkGroups)) {
      debouncedSearchNetworkGroups(search, groupsOptionType);
    }
  }, [
    canPaginateNetworkGroups,
    debouncedSearchNetworkGroups,
    disableCustomValue,
    groupsOptionType,
    labelKey,
    options,
    valueKey,
  ]);

  const handleChange = useCallback((selected) => {
    onChange(selected);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    if (shouldResetOptions) {
      setCustomOption({});
      searchRef.current = '';
    }
    if (onBlur) {
      onBlur();
    }
  }, [shouldResetOptions, onBlur]);

  const filteredOptions = buildFilteredOptions({
    customOption,
    options,
    searchTerm: searchRef.current,
    canPaginateNetworkGroups,
    labelKey,
  });

  return (
    <SimpleSelectField
      className={className || 'single-select-with-custom-value'}
      id={id}
      label={label}
      hideLabel={hideLabel}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onInputChange={handleSearch}
      options={filteredOptions}
      optionSettings={{ labelKey, valueKey }}
      placeholder={placeholder}
      searchable={shouldSearchOnChange}
      searchPlaceholder={searchPlaceholderValue}
      error={error}
    />
  );
}

SingleSelectWithCustomValue.propTypes = {
  canPaginateNetworkGroups: PropTypes.bool.isRequired,
  className: PropTypes.string,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  disableCustomValue: PropTypes.bool,
  error: PropTypes.string,
  groupsOptionType: PropTypes.string,
  hideLabel: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  resetOptions: PropTypes.bool,
  searchPlaceholderValue: PropTypes.string,
  shouldSearchOnChange: PropTypes.bool,
  value: PropTypes.any,
  valueKey: PropTypes.string,
};

SingleSelectWithCustomValue.defaultProps = {
  className: '',
  disableCustomValue: false,
  error: undefined,
  groupsOptionType: '',
  hideLabel: false,
  label: '',
  labelKey: 'label',
  onBlur: _.noop,
  onChange: _.noop,
  options: [],
  placeholder: '',
  resetOptions: true,
  searchPlaceholderValue: '',
  shouldSearchOnChange: true,
  value: undefined,
  valueKey: 'value',
};

export default SingleSelectWithCustomValue;
